import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Factory, ClipboardList, PackageCheck, Layers } from "lucide-react";
import { MyKRA } from "@/pages/admin/AdminWidgets";

// MG Rugs workspace landing. Shows POs routed via this MG (commercial fields stripped
// on backend). Read-only listing + shortcut tiles into Inspections & Yarn Ledger.
export default function MGDashboard() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "hi" ? hi : en);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/mg/weaver-pos")
      .then((r) => setPos(r.data || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  const IN_PROGRESS_STATES = new Set(["Issued", "Raw Material Issued", "In Progress (Daura)", "Final QC"]);
  const CLOSED_STATES = new Set(["Received", "Reconciliation", "Closed"]);
  const summary = pos.reduce((acc, p) => {
    acc.total++;
    const s = p.status || "";
    if (IN_PROGRESS_STATES.has(s)) acc.progress++;
    if (CLOSED_STATES.has(s)) acc.closed++;
    return acc;
  }, { total: 0, progress: 0, closed: 0 });

  return (
    <div data-testid="mg-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
          {t("MG Rugs · Workspace", "MG रग्स · वर्कस्पेस")}
        </h1>
      </div>
      <p className="text-xs text-[#7C8E9E] mb-4">
        {t("Operational view — inspections, weaver POs, and yarn ledger for weavers routed through MG Rugs. No HAG commercial data is shown here.",
           "संचालन दृश्य — MG रग्स के माध्यम से रूट किए बुनकरों के निरीक्षण, PO और यार्न रिकॉर्ड। HAG के व्यावसायिक आँकड़े यहाँ नहीं।")}
      </p>

      {err && <div className="mb-3 text-xs bg-red-50 border border-red-200 rounded p-2">{err}</div>}

      <div className="mb-4"><MyKRA /></div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="hag-surface p-3" data-testid="mg-tile-total">
          <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">{t("Total POs", "कुल PO")}</div>
          <div className="text-2xl font-bold mt-1" style={{ color: "var(--brand-navy)" }}>{summary.total}</div>
        </div>
        <div className="hag-surface p-3" data-testid="mg-tile-progress">
          <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">{t("In progress", "प्रगति में")}</div>
          <div className="text-2xl font-bold mt-1" style={{ color: "var(--brand-navy)" }}>{summary.progress}</div>
        </div>
        <div className="hag-surface p-3" data-testid="mg-tile-closed">
          <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">{t("Closed / Received", "बंद / प्राप्त")}</div>
          <div className="text-2xl font-bold mt-1" style={{ color: "var(--brand-navy)" }}>{summary.closed}</div>
        </div>
        <Link to="/mg/yarn" className="hag-surface p-3 hover:bg-[#FAF6F0] flex flex-col justify-between" data-testid="mg-tile-yarn">
          <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">{t("Yarn ledger", "यार्न लेजर")}</div>
          <div className="mt-1 flex items-center gap-2 text-[#1B3A5C]"><Layers size={20} /> <span className="font-semibold">{t("Log purchases →", "खरीद दर्ज करें →")}</span></div>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <Link to="/mg/inspections" className="hag-surface p-4 hover:bg-[#FAF6F0] flex items-center gap-3" data-testid="mg-link-inspections">
          <ClipboardList size={22} className="text-[#C8A86B]" />
          <div>
            <div className="font-semibold" style={{ color: "var(--brand-navy)" }}>{t("Loom Inspections (read-only)", "लूम निरीक्षण (केवल पढ़ें)")}</div>
            <div className="text-xs text-[#7C8E9E]">{t("View HAG inspections on your weavers, add comments.", "अपने बुनकरों के HAG निरीक्षण देखें, टिप्पणी दें।")}</div>
          </div>
        </Link>
        <Link to="/mg/yarn" className="hag-surface p-4 hover:bg-[#FAF6F0] flex items-center gap-3" data-testid="mg-link-yarn">
          <PackageCheck size={22} className="text-[#C8A86B]" />
          <div>
            <div className="font-semibold" style={{ color: "var(--brand-navy)" }}>{t("Yarn purchases & summary", "यार्न खरीद व सारांश")}</div>
            <div className="text-xs text-[#7C8E9E]">{t("Log Royal Indian Carpets purchases, dye-lot & kg per weaver.", "Royal Indian Carpets की खरीद, डाई-लॉट व किलो प्रति बुनकर।")}</div>
          </div>
        </Link>
      </div>

      <h2 className="text-sm font-bold uppercase tracking-widest text-[#4A627A] mb-2 flex items-center gap-2">
        <Factory size={14} /> {t("Weaver POs via MG", "MG के माध्यम से PO")}
      </h2>
      {loading ? <div className="text-sm text-[#7C8E9E]">Loading…</div> : (
        <div className="hag-surface overflow-x-auto" data-testid="mg-po-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E] bg-[#FAF6F0]">
                <th className="text-left px-3 py-2">{t("PO Number", "PO नंबर")}</th>
                <th className="text-left px-3 py-2">{t("Weaver", "बुनकर")}</th>
                <th className="text-left px-3 py-2">{t("Status", "स्थिति")}</th>
                <th className="text-left px-3 py-2">{t("ETA", "डिलीवरी")}</th>
                <th className="text-left px-3 py-2">{t("Dye lot", "डाई लॉट")}</th>
              </tr>
            </thead>
            <tbody>
              {pos.map((p) => (
                <tr key={p.id} className="border-t border-[#1B3A5C]/10" data-testid={`mg-po-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{p.po_number}</td>
                  <td className="px-3 py-2">{p.weaver_name || "—"}</td>
                  <td className="px-3 py-2"><span className="status-chip pace-none">{p.status}</span></td>
                  <td className="px-3 py-2 text-xs">{p.eta ? new Date(p.eta).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2 text-xs">{p.expected_dye_lot || "—"}</td>
                </tr>
              ))}
              {pos.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-[#7C8E9E]">
                  {t("No POs routed through MG yet.", "MG के माध्यम से अभी कोई PO नहीं।")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
